import { Node as NodeExtension, mergeAttributes } from '@tiptap/core'
import { GapCursor } from '@tiptap/pm/gapcursor'
import { Slice } from '@tiptap/pm/model'
import { Selection } from '@tiptap/pm/state'
import { ReplaceAroundStep, findWrapping } from '@tiptap/pm/transform'
import { findNodeOfType } from '../utils'

export interface CenterJustifyOptions {
  /**
   * The classname to apply to the center-justified wrapper.
   * Defaults to 'text-center-justify'.
   */
  className: string
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    centerJustify: {
      setCenterJustified: (enabled: boolean) => ReturnType
      toggleCenterJustified: () => ReturnType
    }
  }
}

/**
 * This extension allows the document to be "center justified". It wraps
 * all block nodes in a wrapper <div> when enabled, and unwraps them when
 * disabled.
 *
 * This is version 2 of the extension. V1 applied a global `centerJustify`
 * attribute to nodes that were direct descendants of the top-level node.
 *
 * CSS styling is required for the extension to have an effect. Add a rule
 * for the configured `className` to your content styles. For example:
 *
 *   .text-center-justify {
 *     margin-left: auto,
 *     margin-right: auto,
 *     max-width: 640px
 *   }
 */
export const CenterJustify = NodeExtension.create<CenterJustifyOptions>({
  name: 'centerJustify',
  group: 'block',
  content: 'block+',
  isolating: true,
  selectable: false,

  addOptions() {
    return {
      className: 'text-center-justify',
    }
  },

  addKeyboardShortcuts() {
    const isValid = (selection: Selection): boolean =>
      selection.anchor === selection.head &&
      selection.$head.node(1)?.type === this.type

    // Prevent arrow keys from escaping the wrapper
    // GapCursor selections need special handling for up/down, because
    // calling .start/.end on a GapCursor's resolved position will
    // return the start/end of the document, even if there's an adjacent
    // node that we could navigate to
    return {
      ArrowUp: ({ editor: { state, view } }) => {
        const { selection } = state
        if (!isValid(selection) || !view.endOfTextblock('up', state)) {
          return false
        }

        const head =
          selection instanceof GapCursor
            ? selection.head
            : selection.$head.start(selection.$head.depth)

        return head - 2 <= 0
      },
      ArrowLeft: ({ editor: { state, view } }) => {
        const { selection } = state
        if (!isValid(selection) || !view.endOfTextblock('left', state)) {
          return false
        }

        return selection.head - 2 <= 0
      },
      ArrowDown: ({ editor: { state, view } }) => {
        const { doc, selection } = state
        if (!isValid(selection) || !view.endOfTextblock('down', state)) {
          return false
        }

        const head =
          selection instanceof GapCursor
            ? selection.head
            : selection.$head.end(selection.$head.depth)

        return head + 2 >= doc.content.size
      },
      ArrowRight: ({ editor: { state, view } }) => {
        const { doc, selection } = state
        if (!isValid(selection) || !view.endOfTextblock('right', state)) {
          return false
        }

        return selection.head + 2 >= doc.content.size
      },
    }
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      mergeAttributes(HTMLAttributes, { class: this.options.className }),
      0,
    ]
  },

  parseHTML() {
    return [
      {
        tag: 'div',
        getAttrs: (node) =>
          node.classList.contains(this.options.className) && null,
      },
    ]
  },

  addCommands() {
    return {
      setCenterJustified:
        (enabling) =>
        ({ tr, state: { schema }, dispatch }) => {
          const centerJustifyResult = findNodeOfType(tr.doc, this.type)

          if (enabling) {
            // Check if document is already wrapped
            if (centerJustifyResult !== null) {
              return false
            }

            // Resolve inner start & end positions of this node
            const $from = tr.doc.resolve(0)
            const $to = tr.doc.resolve(tr.doc.content.size - 1)
            const range = $from.blockRange($to)
            const wrappingNodes =
              range && findWrapping(range, schema.nodes[this.name], {})
            const canWrap = range !== null && wrappingNodes !== null

            if (canWrap && dispatch) {
              tr.wrap(range, wrappingNodes)
            }

            return canWrap
          } else {
            // Check if document isn't wrapped
            if (centerJustifyResult === null) {
              return false
            }

            const { node, pos: from } = centerJustifyResult
            const to = from + node.nodeSize

            if (dispatch) {
              // From & to are the start/end positions of this node
              // gapFrom & gapTo are the start/end positions of the node's content
              const step = new ReplaceAroundStep(
                from, // from
                to, // to
                from + 1, // gapFrom
                to - 1, // gapTo
                Slice.empty, // slice
                0, // insert
                true // structure
              )
              const stepResult = tr.maybeStep(step)
              return stepResult.doc !== null
            }

            return from < to
          }
        },
      toggleCenterJustified:
        () =>
        ({ commands, state: { doc } }) => {
          const enabled = findNodeOfType(doc, this.type) !== null
          return commands.setCenterJustified(!enabled)
        },
    }
  },
})
